import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactLoading from "react-loading";
import { format } from "date-fns";
import { FiArrowUpRight, FiArrowDownLeft, FiCalendar, FiEdit2 } from "react-icons/fi";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import { showToast } from "../../../../components/CustomToast";
import { useAuth } from "../../../../hooks/AuthContext";
import { i18n } from "../../../../localization/i18n";
import api from "../../../../services/api";
import { Table, Column, TableAction } from "../../../../components/Table";
import { LoadMoreButton } from "../../../../components/LoadMoreButton";
import { roles } from "../../../../utils/constants";
import { ITransaction } from "./dtos";
import {
  Container,
  TransactionsTableWrapper,
} from "./styles";
import NoInfo from "../../../../components/NoInfo";
import { FixcoinPaymentDetails } from "../../../../components/FixcoinPaymentDetails";
import { AddFixcoinModal } from "../../../Licensee/components/AddFixcoinModal";

interface TransactionsProps {
  licenseeId?: string;
  hideNavigationAction?: boolean;
  onSuccess?: () => void;
}

const getTransactionColor = (type: "income" | "outcome") => {
  return type === "income" ? "var(--active-green)" : "var(--error)";
};

const formatTransactionDate = (dateVal?: Date | string) => {
  try {
    if (dateVal) {
      const d = new Date(String(dateVal).replace("Z", ""));
      if (!isNaN(d.getTime())) {
        return format(d, "dd/MM/yyyy' 'HH:mm");
      }
    }
  } catch {
    return "-";
  }
  return "-";
};

export function Transactions({
  licenseeId,
  hideNavigationAction = false,
  onSuccess,
}: TransactionsProps = {}) {
  const { user, currency, licenseeFixcoins, setNavigationAction, reloadUser } = useAuth();
  const [transactions, setTransactions] = useState<ITransaction[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const [isFixcoinModalOpen, setIsFixcoinModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<ITransaction | null>(null);

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [limit] = useState(10);

  const isAdmin = user?.role === roles.admin;
  const targetLicenseeId = licenseeId || user?.person?.licensee?.id || "";

  function loadMore() {
    setPage((prev) => prev + 1);
  }

  async function GetLicenseeTransactions(reload: boolean) {
    setLoading(true);

    const queryList = [];

    if (targetLicenseeId) {
      queryList.push(`licensee_id=${targetLicenseeId}`);
    }
    queryList.push(`limit=${limit}`);
    !reload && queryList.push(`page=${page}`);

    try {
      const data = await api.get(`/transactions?${queryList.join("&")}`);
      const newTransactions = data?.data?.transactions || [];
      const total = data?.data?.total || 0;
      setTransactions(
        reload
          ? newTransactions
          : [...transactions, ...newTransactions]
      );
      setTotalPage(Math.ceil(total / limit) || 1);
    } catch (error) {
      showToast({
        message: "Não foi possível resgatar suas transações",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setPage(1);
    GetLicenseeTransactions(true);
    if (!hideNavigationAction) {
      setNavigationAction({
        hasButton: false,
        description: "FixCoins",
      });
    }
  }, [licenseeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page > 1) {
      GetLicenseeTransactions(false);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.openFixcoinModal) {
      setIsFixcoinModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const columns = useMemo<Column<ITransaction>[]>(
    () => [
      {
        key: "description",
        header: "Descrição",
        render: (row) => {
          const color = getTransactionColor(row.type);
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
                <span
                  style={{ fontWeight: 600, fontSize: "15px", color }}
                  title={row.description}
                >
                  {row.description || "Transação"}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "var(--gray)", fontSize: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <FiCalendar /> {formatTransactionDate(row.transaction_date)}
                </div>
                {row.code && (
                  <>
                    <span>•</span>
                    <span>Ref: {row.code}</span>
                  </>
                )}
              </div>
            </div>
          );
        },
      },
      {
        key: "type",
        header: "Tipo",
        render: (row) => {
          const isIncome = row.type === "income";
          const color = getTransactionColor(row.type);
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px", justifyContent: "center", height: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", color, fontWeight: 600, fontSize: "13px" }}>
                {isIncome ? <FiArrowUpRight size={14} /> : <FiArrowDownLeft size={14} />}
                <span>{isIncome ? "Entrada" : "Saída"}</span>
              </div>
            </div>
          );
        },
      },
      {
        key: "ammount",
        header: "FixCoins",
        render: (row) => {
          const num = Number(row.ammount || 0);
          const formattedAmount = num.toLocaleString("pt-BR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", justifyContent: "center", height: "100%" }}>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontWeight: 600,
                  color: "var(--black)",
                  fontSize: "14px",
                }}
              >
                <RiMoneyDollarCircleLine size={16} color="var(--fixit)" />
                {formattedAmount}
              </span>
            </div>
          );
        },
      },
    ],
    []
  );

  const actions = useMemo<TableAction<ITransaction>[]>(
    () => [
      {
        label: "Editar",
        icon: <FiEdit2 size={16} />,
        onClick: (row) => {
          setEditingTransaction(row);
        },
      },
    ],
    []
  );

  return (
    <Container style={licenseeId ? { marginTop: 0 } : undefined}>
      {!licenseeId && (
        <FixcoinPaymentDetails
          isOpen={isFixcoinModalOpen}
          onHandleCancel={() => setIsFixcoinModalOpen(false)}
          currency={currency}
          licenseeFixcoins={licenseeFixcoins}
          onSuccess={() => {
            GetLicenseeTransactions(true);
            reloadUser();
          }}
        />
      )}

      {editingTransaction && (
        <AddFixcoinModal
          open={Boolean(editingTransaction)}
          licensee_id={targetLicenseeId}
          transaction={editingTransaction}
          onHandleCloseRequest={() => setEditingTransaction(null)}
          onSuccess={() => {
            setEditingTransaction(null);
            GetLicenseeTransactions(true);
            reloadUser();
            if (onSuccess) onSuccess();
          }}
        />
      )}

      {loading && transactions?.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
          <ReactLoading
            type={"bubbles"}
            color={"var(--fixit)"}
            height={64}
            width={64}
          />
        </div>
      ) : transactions?.length === 0 ? (
        <NoInfo noInfo={`${i18n.t(`noInfo.transaction`)}`} />
      ) : (
        <>
          <TransactionsTableWrapper>
            <Table
              columns={columns}
              data={transactions}
              actions={isAdmin ? actions : undefined}
            />
          </TransactionsTableWrapper>

          <LoadMoreButton
            loading={loading && page > 1}
            hasMore={page < totalPage}
            onClick={loadMore}
            label="Carregar mais transações"
          />
        </>
      )}
    </Container>
  );
}
export default Transactions;
